import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listSessions } from '../api/sessions';
import type { SessionSummary } from '../types/api';
import { StatusBadge } from '../components/sessions/StatusBadge';

type FilterStatus = 'all' | 'active' | 'ended';

export function SessionsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<SessionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('all');

  async function load(status: FilterStatus) {
    setLoading(true);
    setError(null);
    try {
      const res = await listSessions({
        pageSize: 50,
        ...(status !== 'all' ? { status } : {}),
      });
      setItems(res.items);
    } catch {
      setError('Failed to load sessions.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(filter); }, [filter]);

  const filters: { label: string; value: FilterStatus }[] = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Ended', value: 'ended' },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Sessions</h1>
        <button
          className="bg-teal-600 dark:bg-teal-500 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-teal-700 dark:hover:bg-teal-600 disabled:opacity-50 transition-colors cursor-pointer"
          onClick={() => navigate('/sessions/new')}
        >
          Start session
        </button>
      </div>

      <div className="flex gap-2">
        {filters.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
              filter === f.value
                ? 'bg-teal-600 dark:bg-teal-500 text-white'
                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-10 shadow-sm text-center">
          <p className="text-slate-500 dark:text-slate-400">No sessions yet. Start your first session!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(session => (
            <button
              key={session.id}
              onClick={() => navigate(`/sessions/${session.id}`)}
              className="w-full text-left rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm hover:border-teal-300 dark:hover:border-teal-700 hover:shadow-md transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-semibold text-slate-900 dark:text-slate-50 truncate">
                      {session.name}
                    </span>
                    <StatusBadge status={session.status} />
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span className="text-sm text-slate-500 dark:text-slate-400">{session.spotName}</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {new Date(session.startTime).toLocaleString()}
                    </span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {session.participantCount} {session.participantCount === 1 ? 'participant' : 'participants'}
                    </span>
                  </div>
                </div>
                <span className="text-slate-400 dark:text-slate-500 text-lg leading-none mt-0.5">›</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
